import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Waves,
    ChevronsLeft,
    AlertCircle,
    CheckCircle,
    ShoppingCart,
    Users,
    Info,
    Layers,
    ArrowRight,
    Lock,
    RefreshCw,
} from 'lucide-react';
import marketingService from '../services/marketingService';
import TreeStructure from './TreeStructure';

// ──────────────────────────────────────────────────────────────────────────
//  LIGHT RIVERS — кластер в двух фазах:
//   1) ПРЕДЗАПУСК — обратный отсчёт до старта + покупка одного стартового места
//      в предварительную очередь (расстановка ближе к старту).
//   2) ПОСЛЕ СТАРТА — маркетинг-план + «мои места» по уровням с прогрессом
//      закрытия (read-only overview). Интерактив дерева/брони/ТК — следующий этап.
//
//  Дату старта приоритетно отдаёт бэк (launch_at в ответах). Локальная
//  константа — фолбэк, если бэк ещё не ответил.
// ──────────────────────────────────────────────────────────────────────────
const LAUNCH_AT_FALLBACK = '2026-06-27T00:00:00+03:00';

const ENTRY_PRICE = 50;
const ACTIVATION_PART = 45;
const COMMUNITY_PART = 5;

// Маркетинг-план (для отображения). Цифры из ТЗ Light Rivers.
const PLAN = [
    {
        nl: 1, title: '1 уровень', places: '1–3', entry: 45, exit: 135,
        rules: [
            'Закрытие 1 и 2 места: 90$ — переход на 2 уровень',
            'Закрытие 3 места: 45$ — на вывод',
        ],
    },
    {
        nl: 2, title: '2 уровень', sub: 'накопительный', places: '1–2', entry: 90, exit: 180,
        rules: ['Закрытие 1 и 2 места: 180$ — переход на 3 уровень'],
    },
    {
        nl: 3, title: '3 уровень', places: '1–2', entry: 180, exit: 360,
        rules: [
            'Закрытие 1 места: 45$ — 1 ТК в общий банк',
            'Закрытие 2 места: 30$ вывод + 10$ реферальное + 275$ переход на 4 уровень',
        ],
    },
    {
        nl: 4, title: '4 уровень', places: '1–2', entry: 275, exit: 550,
        rules: [
            'Закрытие 1 и 2 места: 250 / 150 / 100$ на вывод (по числу активных личников)',
            '2 место: +45$ реинвест в 1 уровень + 5$ на комьюнити',
        ],
    },
];

const pad = (n) => String(n).padStart(2, '0');

const LightRiversCluster = ({ onBack }) => {
    const [loading, setLoading] = useState(true);

    // Предзаказ
    const [isPreordered, setIsPreordered] = useState(false);
    const [position, setPosition] = useState(null);
    const [totalInQueue, setTotalInQueue] = useState(null);
    const [createdAt, setCreatedAt] = useState(null);
    const [preorderStatus, setPreorderStatus] = useState(null); // pending|placed|failed

    // Фаза/таймер
    const [launchAt, setLaunchAt] = useState(LAUNCH_AT_FALLBACK);
    const [placementStarted, setPlacementStarted] = useState(false);
    const [remaining, setRemaining] = useState(null);
    const [isLaunched, setIsLaunched] = useState(false);

    // Overview (после старта)
    const [levels, setLevels] = useState([]);

    // Действия
    const [buying, setBuying] = useState(false);
    const [joining, setJoining] = useState(false);
    const [isJoined, setIsJoined] = useState(false);
    const [actionMessage, setActionMessage] = useState(null);
    const [treeTarget, setTreeTarget] = useState(null); // { level, numberLevel, mentorId }
    const [searchParams] = useSearchParams();

    // Прямая ссылка (кнопка «Апгрейд»): ?level=..&mentor=.. — открываем дерево на этом узле.
    useEffect(() => {
        const NOMINAL_TO_NL = { 45: 1, 90: 2, 180: 3, 275: 4 };
        const lvl = parseInt(searchParams.get('level'), 10);
        const mentor = searchParams.get('mentor');
        if (lvl && NOMINAL_TO_NL[lvl] && mentor !== null) {
            autoOpened.current = true; // не давать авто-открытию минимального уровня перебить
            setTreeTarget({
                level: lvl,
                numberLevel: NOMINAL_TO_NL[lvl],
                mentorId: parseInt(mentor, 10) || 0,
            });
        }
        // только при первой загрузке
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const autoOpened = useRef(false);
    const offsetRef = useRef(0);            // серверное_время - клиентское (мс)
    const lastSyncRef = useRef(0);          // троттлинг кнопки синхронизации
    const [syncing, setSyncing] = useState(false);

    // Минимальный уровень, где у пользователя есть место (вход в дерево по умолчанию)
    const computeMinActiveTarget = (lvls) => {
        const withPlaces = (lvls || []).filter((l) => (l.my_places_count || 0) > 0);
        if (!withPlaces.length) return null;
        const minLv = withPlaces.reduce((a, b) => (b.number_level < a.number_level ? b : a));
        const places = minLv.my_places || [];
        const open = places.find((p) => !p.is_closed) || places[0];
        return { level: minLv.level, numberLevel: minLv.number_level, mentorId: open?.marketing_id || 0 };
    };

    const load = useCallback(async () => {
        try {
            const [statusRes, overviewRes] = await Promise.all([
                marketingService.getLightRiversPreorderStatus(),
                marketingService.getLightRiversOverview(),
            ]);

            let launchedNow = false;

            if (statusRes && statusRes.success) {
                const d = statusRes.data || {};
                if (d.server_time) {
                    const parsed = new Date(d.server_time).getTime();
                    if (!Number.isNaN(parsed)) offsetRef.current = parsed - Date.now();
                }
                setIsPreordered(!!d.is_preordered);
                setIsJoined(!!d.is_joined);
                setPosition(d.position ?? null);
                setTotalInQueue(d.total ?? null);
                setCreatedAt(d.created_at ?? null);
                if (d.status) setPreorderStatus(d.status);
                if (d.launch_at) setLaunchAt(d.launch_at);
                if (d.launched) launchedNow = true;
            }

            if (overviewRes && overviewRes.success) {
                const o = overviewRes.data || {};
                setPlacementStarted(!!o.placement_started);
                setLevels(Array.isArray(o.levels) ? o.levels : []);
                if (o.launch_at) setLaunchAt(o.launch_at);
                if (o.preorder) {
                    setPreorderStatus(o.preorder.status ?? null);
                    if (o.preorder.is_preordered) setIsPreordered(true);
                }

                // Вход в кластер сразу открывает дерево на минимальном уровне,
                // где пользователь активен (без промежуточного выбора уровня).
                if (!autoOpened.current && launchedNow) {
                    const target = computeMinActiveTarget(o.levels);
                    if (target) {
                        autoOpened.current = true;
                        setTreeTarget(target);
                    }
                }
            }
        } catch (e) {
            console.error('Light Rivers load error:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Таймер (с поправкой на серверное время, чтобы не дрейфовал)
    useEffect(() => {
        const target = new Date(launchAt).getTime();
        const tick = () => {
            const dist = target - (Date.now() + offsetRef.current);
            if (dist <= 0) {
                setRemaining(0);
                setIsLaunched(true);
                return true;
            }
            setRemaining(dist);
            setIsLaunched(false);
            return false;
        };
        if (tick()) return;
        const id = setInterval(() => {
            if (tick()) clearInterval(id);
        }, 1000);
        return () => clearInterval(id);
    }, [launchAt]);

    const handlePreorder = async () => {
        if (buying || isPreordered || isLaunched) return;
        setBuying(true);
        setActionMessage(null);
        try {
            const res = await marketingService.createLightRiversPreorder();
            if (res && res.success) {
                const d = res.data || {};
                setIsPreordered(true);
                setPosition(d.position ?? null);
                setTotalInQueue(d.total ?? null);
                setCreatedAt(d.created_at ?? null);
                setActionMessage({
                    type: 'success',
                    text: 'Место зарезервировано в предварительной очереди. Расстановка — ближе к старту.',
                });
            } else {
                setActionMessage({ type: 'error', text: res?.message || 'Не удалось зарезервировать место.' });
            }
        } catch (e) {
            console.error('Light Rivers preorder error:', e);
            setActionMessage({ type: 'error', text: 'Ошибка соединения с сервером.' });
        } finally {
            setBuying(false);
        }
    };

    // Синхронизация таймера с серверным временем (троттлинг 4с, чтобы не спамили)
    const handleSync = async () => {
        const now = Date.now();
        if (syncing || now - lastSyncRef.current < 4000) return;
        lastSyncRef.current = now;
        setSyncing(true);
        try {
            const res = await marketingService.getLightRiversPreorderStatus();
            if (res && res.success && res.data && res.data.server_time) {
                const parsed = new Date(res.data.server_time).getTime();
                if (!Number.isNaN(parsed)) offsetRef.current = parsed - Date.now();
                if (res.data.launch_at) setLaunchAt(res.data.launch_at);
            }
        } catch (e) {
            console.error('Light Rivers time sync error:', e);
        } finally {
            setTimeout(() => setSyncing(false), 800);
        }
    };

    const handleBuyAndJoin = async () => {
        if (buying || joining) return;
        setBuying(true);
        setActionMessage(null);
        try {
            const res = await marketingService.createLightRiversPreorder();
            if (!res || !res.success) {
                setActionMessage({ type: 'error', text: res?.message || 'Не удалось купить место.' });
                return;
            }
            const d = res.data || {};
            setIsPreordered(true);
            setPosition(d.position ?? null);
            setTotalInQueue(d.total ?? null);
            setCreatedAt(d.created_at ?? null);

            const jr = await marketingService.joinLightRivers();
            if (jr && jr.success) {
                setIsJoined(true);
                setActionMessage({ type: 'success', text: 'Место куплено, вы в списке участников.' });
            } else {
                setActionMessage({ type: 'error', text: jr?.message || 'Место куплено. Нажмите «Участвовать», чтобы попасть в список.' });
            }
        } catch (e) {
            console.error('Light Rivers buy-and-join error:', e);
            setActionMessage({ type: 'error', text: 'Ошибка соединения с сервером.' });
        } finally {
            setBuying(false);
        }
    };

    const handleJoin = async () => {
        if (joining || isJoined) return;
        setJoining(true);
        setActionMessage(null);
        try {
            const res = await marketingService.joinLightRivers();
            if (res && res.success) {
                setIsJoined(true);
                setActionMessage({
                    type: 'success',
                    text: 'Вы в списке участников. Расстановка пройдёт по очереди.',
                });
            } else {
                setActionMessage({ type: 'error', text: res?.message || 'Не удалось вступить в список участников.' });
            }
        } catch (e) {
            console.error('Light Rivers join error:', e);
            setActionMessage({ type: 'error', text: 'Ошибка соединения с сервером.' });
        } finally {
            setJoining(false);
        }
    };

    // Фаза «после старта» — СТРОГО по таймеру (когда он дойдёт до launch_at).
    // Дата расстановки (placement_start) на появление кнопки участия НЕ влияет.
    const launched = isLaunched;

    const t = remaining ?? 0;
    const totalSec = Math.max(0, Math.floor(t / 1000));
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;

    const launchDateLabel = new Date(launchAt).toLocaleString('ru-RU', {
        day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow',
    });

    const TimeCell = ({ value, label }) => (
        <div className="flex flex-col items-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/25 flex items-center justify-center shadow-lg">
                <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tabular-nums">{pad(value)}</span>
            </div>
            <span className="mt-2 text-[10px] sm:text-xs uppercase tracking-wider text-cyan-100/90">{label}</span>
        </div>
    );

    // Открыть конкретный уровень в дереве, начиная с незакрытого места
    // (под которым есть свободные позиции); иначе — с первого места уровня.
    const openLevel = (lv) => {
        const places = lv.my_places || [];
        if (!places.length) return;
        const open = places.find((p) => !p.is_closed) || places[0];
        setTreeTarget({
            level: lv.level,
            numberLevel: lv.number_level,
            mentorId: open.marketing_id || 0,
        });
    };

    if (treeTarget) {
        return (
            <TreeStructure
                program={6}
                tarif="rivers"
                level={treeTarget.level}
                numberLevel={treeTarget.numberLevel}
                initialMentorId={treeTarget.mentorId}
                onBack={() => setTreeTarget(null)}
            />
        );
    }

    return (
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Шапка */}
            <div className="relative bg-gradient-to-br from-cyan-500 via-teal-500 to-sky-600 px-4 py-6 md:px-8 md:py-8 text-white overflow-hidden">
                <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/10" />
                <div className="absolute -left-8 -bottom-12 w-44 h-44 rounded-full bg-white/5" />

                <div className="relative flex items-center justify-between mb-5">
                    {onBack && (
                        <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-white/90 hover:text-white transition-colors">
                            <ChevronsLeft size={18} /> Назад
                        </button>
                    )}
                    <span className="ml-auto text-[10px] font-semibold px-2.5 py-1 bg-white/20 rounded-full uppercase tracking-wide">
                        {launched ? 'Кластер активен' : 'Скоро запуск'}
                    </span>
                </div>

                <div className="relative flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                        <Waves size={26} className="text-white" />
                    </div>
                    <div>
                        <h2 className="text-xl md:text-2xl font-bold">Light Rivers</h2>
                        <p className="text-cyan-50/90 text-xs md:text-sm">
                            {launched ? 'Кластер запущен' : `Старт ${launchDateLabel} (МСК)`}
                        </p>
                    </div>
                </div>

                {!launched && (
                    <div className="relative">
                        <p className="text-center text-sm text-cyan-50/90 mb-3">До старта осталось</p>
                        <div className="flex items-start justify-center gap-2 sm:gap-3 md:gap-4">
                            <TimeCell value={days} label="дней" />
                            <span className="text-2xl md:text-4xl font-bold text-white/70 mt-4 sm:mt-5 md:mt-7">:</span>
                            <TimeCell value={hours} label="часов" />
                            <span className="text-2xl md:text-4xl font-bold text-white/70 mt-4 sm:mt-5 md:mt-7">:</span>
                            <TimeCell value={minutes} label="минут" />
                            <span className="text-2xl md:text-4xl font-bold text-white/70 mt-4 sm:mt-5 md:mt-7">:</span>
                            <TimeCell value={seconds} label="секунд" />
                        </div>
                        <div className="mt-4 flex justify-center">
                            <button
                                onClick={handleSync}
                                disabled={syncing}
                                className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/30 text-cyan-50/90 transition-colors ${
                                    syncing ? 'opacity-60 cursor-wait' : 'hover:bg-white/10'
                                }`}
                            >
                                <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />
                                {syncing ? 'Синхронизация…' : 'Синхронизировать время'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Тело */}
            <div className="p-4 md:p-6">
                {loading ? (
                    <div className="py-12 flex flex-col items-center justify-center text-gray-500">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-500 mb-3" />
                        <p className="text-sm">Загрузка…</p>
                    </div>
                ) : (
                    <>
                        {/* Условия входа */}
                        <div className="mb-5 bg-gradient-to-r from-cyan-50 to-teal-50 border-2 border-cyan-200 rounded-2xl p-4 md:p-5">
                            <h4 className="font-bold text-gray-800 text-base md:text-lg mb-3 flex items-center gap-2">
                                <Info size={18} className="text-cyan-600" /> Условия входа
                            </h4>
                            <div className="space-y-2 text-sm">
                                <div className="flex items-center justify-between">
                                    <span className="text-gray-600">Вход (стартовое место)</span>
                                    <span className="font-bold text-cyan-700">{ENTRY_PRICE}$</span>
                                </div>
                                <div className="flex items-center justify-between text-gray-500">
                                    <span className="pl-3">• активация бизнес-места</span><span>{ACTIVATION_PART}$</span>
                                </div>
                                <div className="flex items-center justify-between text-gray-500">
                                    <span className="pl-3">• развитие комьюнити</span><span>{COMMUNITY_PART}$</span>
                                </div>
                            </div>
                        </div>

                        {/* Сообщение о действии */}
                        {actionMessage && (
                            <div className={`mb-5 rounded-xl p-3 border-2 flex items-start gap-2 ${
                                actionMessage.type === 'success'
                                    ? 'bg-green-50 border-green-200 text-green-700'
                                    : 'bg-red-50 border-red-200 text-red-700'
                            }`}>
                                {actionMessage.type === 'success'
                                    ? <CheckCircle size={18} className="mt-0.5 flex-shrink-0" />
                                    : <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />}
                                <span className="text-sm">{actionMessage.text}</span>
                            </div>
                        )}

                        {/* ── ПОСЛЕ СТАРТА: купить+участвовать / участвовать / статус ── */}
                        {launched && (
                            !isPreordered ? (
                                <div className="mb-6 bg-gradient-to-br from-cyan-50 to-teal-50 border-2 border-cyan-200 rounded-2xl p-6 text-center">
                                    <p className="font-bold text-gray-800 text-lg mb-1">Старт открыт</p>
                                    <p className="text-xs text-gray-500 mb-4">Вход {ENTRY_PRICE}$ · сразу в список расстановки</p>
                                    <button
                                        onClick={handleBuyAndJoin}
                                        disabled={buying || joining}
                                        className={`w-full max-w-md mx-auto px-8 py-4 rounded-2xl font-bold text-base flex items-center justify-center gap-2 shadow-lg transition-all ${
                                            (buying || joining) ? 'bg-cyan-300 text-white cursor-wait opacity-80'
                                                : 'bg-gradient-to-r from-cyan-500 to-teal-600 text-white hover:from-cyan-600 hover:to-teal-700 hover:shadow-xl transform hover:scale-[1.02]'
                                        }`}
                                    >
                                        {(buying || joining) ? (
                                            <><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" /> Обработка…</>
                                        ) : (
                                            <><ShoppingCart size={20} /> Купить и участвовать — {ENTRY_PRICE}$</>
                                        )}
                                    </button>
                                </div>
                            ) : isJoined ? (
                                <div className="mb-6 rounded-2xl p-4 border-2 bg-cyan-50 border-cyan-200 flex items-center gap-3">
                                    <CheckCircle size={20} className="text-cyan-600 flex-shrink-0" />
                                    <div>
                                        <p className="font-semibold text-gray-800">Вы в списке участников</p>
                                        <p className="text-xs text-gray-500">Расстановка пройдёт по очереди (по времени участия).</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="mb-6 bg-gradient-to-br from-cyan-50 to-teal-50 border-2 border-cyan-200 rounded-2xl p-6 text-center">
                                    <p className="font-bold text-gray-800 text-lg mb-1">Старт открыт — подтвердите участие</p>
                                    <p className="text-xs text-gray-500 mb-4">
                                        Место зарезервировано. Нажмите «Участвовать», чтобы попасть в список перед расстановкой.
                                    </p>
                                    <button
                                        onClick={handleJoin}
                                        disabled={joining}
                                        className={`w-full max-w-md mx-auto px-8 py-4 rounded-2xl font-bold text-base flex items-center justify-center gap-2 shadow-lg transition-all ${
                                            joining ? 'bg-cyan-300 text-white cursor-wait opacity-80'
                                                : 'bg-gradient-to-r from-cyan-500 to-teal-600 text-white hover:from-cyan-600 hover:to-teal-700 hover:shadow-xl transform hover:scale-[1.02]'
                                        }`}
                                    >
                                        {joining ? (
                                            <><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" /> Обработка…</>
                                        ) : (
                                            <><Users size={20} /> Участвовать</>
                                        )}
                                    </button>
                                </div>
                            )
                        )}

                        {/* ── ФАЗА ПРЕДЗАПУСКА: предзаказ ───────────────────────── */}
                        {!launched && (
                            isPreordered ? (
                                <div className="mb-6 bg-white border-2 border-green-200 rounded-2xl overflow-hidden shadow-md">
                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-4 border-b-2 border-green-100 flex items-center gap-2">
                                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                            <CheckCircle size={20} className="text-green-600" />
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-800">Место зарезервировано</p>
                                            <p className="text-xs text-gray-500">Вы в предварительной очереди Light Rivers</p>
                                        </div>
                                    </div>
                                    <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        <div className="bg-gray-50 rounded-xl p-3 text-center">
                                            <p className="text-[11px] text-gray-500 uppercase tracking-wide mb-1">Позиция</p>
                                            <p className="text-2xl font-bold text-cyan-600">{position != null ? `#${position}` : '—'}</p>
                                        </div>
                                        <div className="bg-gray-50 rounded-xl p-3 text-center">
                                            <p className="text-[11px] text-gray-500 uppercase tracking-wide mb-1">Всего в очереди</p>
                                            <p className="text-2xl font-bold text-gray-700">{totalInQueue != null ? totalInQueue : '—'}</p>
                                        </div>
                                        <div className="bg-gray-50 rounded-xl p-3 text-center">
                                            <p className="text-[11px] text-gray-500 uppercase tracking-wide mb-1">Активация</p>
                                            <p className="text-sm font-semibold text-gray-700 pt-1">
                                                {createdAt ? new Date(createdAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="mb-6 bg-white border-2 border-cyan-200 rounded-2xl p-5 shadow-md">
                                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center shadow-md flex-shrink-0">
                                                <Users size={22} className="text-white" />
                                            </div>
                                            <div>
                                                <p className="font-bold text-gray-800">Стартовое место в Light Rivers</p>
                                                <p className="text-xs text-gray-500">Одно место на аккаунт · {ENTRY_PRICE}$</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={handlePreorder}
                                            disabled={buying}
                                            className={`w-full sm:w-auto px-6 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 shadow-md transition-all ${
                                                buying ? 'bg-cyan-300 text-white cursor-wait opacity-80'
                                                    : 'bg-gradient-to-r from-cyan-500 to-teal-600 text-white hover:from-cyan-600 hover:to-teal-700 hover:shadow-lg transform hover:scale-105'
                                            }`}
                                        >
                                            {buying ? (
                                                <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /> Обработка…</>
                                            ) : (
                                                <><ShoppingCart size={18} /> Зарезервировать место — {ENTRY_PRICE}$</>
                                            )}
                                        </button>
                                    </div>
                                    <p className="mt-3 text-xs text-gray-400 leading-relaxed">
                                        Покупка одного стартового места. Оно попадает в предварительную очередь
                                        по дате и времени активации. Дерево расставляется ближе к старту.
                                    </p>
                                </div>
                            )
                        )}

                        {/* ── ФАЗА ПОСЛЕ СТАРТА: мои места по уровням ───────────── */}
                        {launched && (
                            <div className="mb-6">
                                <h4 className="font-bold text-gray-800 text-base md:text-lg mb-3 flex items-center gap-2">
                                    <Layers size={18} className="text-cyan-600" /> Мои места по уровням
                                </h4>

                                {isPreordered && preorderStatus === 'pending' && (
                                    <div className="mb-3 rounded-xl p-3 border-2 bg-amber-50 border-amber-200 text-amber-700 text-sm flex items-center gap-2">
                                        <Info size={16} /> Ваше место в очереди на расстановку. Появится в дереве после обработки.
                                    </div>
                                )}

                                {levels.length === 0 ? (
                                    <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center text-sm text-gray-500">
                                        Пока нет данных по местам.
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {levels.map((lv) => (
                                            <div key={lv.number_level} className="bg-white border-2 border-gray-100 rounded-2xl overflow-hidden">
                                                <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-cyan-50 to-teal-50 border-b border-cyan-100">
                                                    <div className="flex items-center gap-2">
                                                        <span className="w-7 h-7 rounded-lg bg-cyan-500 text-white text-sm font-bold flex items-center justify-center">
                                                            {lv.number_level}
                                                        </span>
                                                        <span className="font-semibold text-gray-800 text-sm">
                                                            {lv.number_level} уровень · номинал {lv.level}$
                                                        </span>
                                                    </div>
                                                    <span className="text-xs text-gray-500">мест на стол: {lv.position_count}</span>
                                                </div>
                                                <div className="p-4">
                                                    {(!lv.my_places || lv.my_places.length === 0) ? (
                                                        <p className="text-xs text-gray-400">Нет активных мест на этом уровне</p>
                                                    ) : (
                                                        <div className="space-y-2">
                                                            {lv.my_places.map((pl, idx) => (
                                                                <div key={pl.marketing_id || idx} className="flex items-center justify-between">
                                                                    <span className="text-xs text-gray-600">Место #{idx + 1}</span>
                                                                    <div className="flex items-center gap-2 flex-1 mx-3">
                                                                        <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                                                                            <div
                                                                                className="h-full bg-gradient-to-r from-cyan-500 to-teal-500"
                                                                                style={{ width: `${Math.min(100, Math.round((pl.filled / Math.max(1, pl.total)) * 100))}%` }}
                                                                            />
                                                                        </div>
                                                                        <span className="text-xs font-semibold text-gray-700 tabular-nums">
                                                                            {pl.filled}/{pl.total}
                                                                        </span>
                                                                    </div>
                                                                    {pl.is_closed
                                                                        ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">закрыто</span>
                                                                        : <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700">открыто</span>}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {lv.my_places && lv.my_places.length > 0 && (
                                                        <button
                                                            onClick={() => openLevel(lv)}
                                                            className="mt-3 w-full px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-teal-600 text-white hover:from-cyan-600 hover:to-teal-700 shadow-sm hover:shadow-md transition-all"
                                                        >
                                                            <Layers size={16} /> Открыть {lv.number_level} уровень
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Маркетинг-план (виден всегда) */}
                        <div>
                            <h4 className="font-bold text-gray-800 text-base md:text-lg mb-3 flex items-center gap-2">
                                <Waves size={18} className="text-cyan-600" /> Маркетинг
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {PLAN.map((lvl) => (
                                    <div key={lvl.nl} className="bg-white border-2 border-gray-100 rounded-2xl p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-600 text-white text-sm font-bold flex items-center justify-center">
                                                    {lvl.nl}
                                                </span>
                                                <div>
                                                    <p className="font-semibold text-gray-800 text-sm leading-tight">{lvl.title}</p>
                                                    {lvl.sub && <p className="text-[10px] text-cyan-600 uppercase tracking-wide">{lvl.sub}</p>}
                                                </div>
                                            </div>
                                            <span className="text-[11px] text-gray-400">места {lvl.places}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm mb-2">
                                            <span className="text-gray-500">вход</span>
                                            <span className="font-bold text-gray-800">{lvl.entry}$</span>
                                            <ArrowRight size={14} className="text-gray-300" />
                                            <span className="text-gray-500">выход</span>
                                            <span className="font-bold text-teal-600">{lvl.exit}$</span>
                                        </div>
                                        <ul className="space-y-1">
                                            {lvl.rules.map((r, i) => (
                                                <li key={i} className="text-xs text-gray-500 flex gap-1.5">
                                                    <span className="text-cyan-400 mt-0.5">•</span><span>{r}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-3 text-[11px] text-gray-400 leading-relaxed">
                                Подписка на комьюнити учитывается во всех кластерах: при её отсутствии
                                упущенная прибыль идёт в комьюнити.
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default LightRiversCluster;
